import { UIView } from "@uirouter/react";
import { omit } from "lodash";
import React, { FunctionComponent, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import LoginBackgroundJPG from "../../assets/login-background.jpg";
import { ReactComponent as LoginLogo } from "../../assets/login-logo.svg";
import { PrimaryButton } from "../../components/button";
import { Checkbox as UnstyledCheckbox } from "../../components/forms/checkbox";
import { Country as UnstyledCountry } from "../../components/forms/country";
import { Select as UnstyledSelect } from "../../components/forms/select";
import { DateField as UnstyledDateField } from "../../components/forms/date";
import { Password as UnstyledPassword } from "../../components/forms/password";
import { TextField as UnstyledTextField } from "../../components/forms/text";
import { useGlobalNotifications } from "../../components/hooks/use-global-notification";
import { Cover, Flex } from "../../components/layout";
import { useDefaultToEnglish } from "../../i18n";
import { colours } from "../../styles/colours";
import { device } from "../../styles/media";

const aspectRatioMediaQuery = "(orientation: landscape)";

const borderRadius = "10px";
namespace Logo {
  export const Logo = styled(LoginLogo)`
    transform: scale(1);
    width: 40%;

    & .icon-primary {
      fill: ${colours.white};
    }

    @media (orientation: landscape) {
      width: 60%;
    }

    @media ${device.tablet} {
      width: 60%;
    }
  `;

  export const LoginBackground = styled.div`
    ${Cover};
    background: url(${LoginBackgroundJPG}) center 70% no-repeat;
    background-size: cover;

    @media ${device.tablet} {
      border-top-left-radius: ${borderRadius};
      border-bottom-left-radius: ${borderRadius};
    }

    @media ${device.laptop} {
      background-position-y: 30%;
    }
  `;

  export const Container = styled(Flex).attrs({ center: true })`
    flex-direction: column;
    flex-grow: 1;
    flex-shrink: 1;
    background: ${colours.white};
    position: relative;
    padding: 2em 0;

    @media ${device.tablet} {
      min-height: auto;
      border-top-left-radius: ${borderRadius};
      border-bottom-left-radius: ${borderRadius};
    }

    @media ${device.laptop} {
      min-width: 0;
    }
  `;

  export const Text = styled.p`
    color: ${colours.purple};
    font-size: 0.9em;
    text-align: center;
  `;
}

const Overlay = styled(Flex).attrs({ center: true })`
  width: 100%;
  min-height: 100%;
  background: ${colours.grey};
  overflow: auto;
  @media ${device.laptop} {
    padding: 2% 0;
  }

  @media (orientation: portrait) {
    height: 100%;
  }
`;

const Container = styled(Flex)`
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  height: 100%;
  font-size: 1.5vmax;

  @media ${aspectRatioMediaQuery} {
    flex-direction: row;
  }

  @media ${device.tablet} {
    flex-direction: row;
    font-size: 1vmax;
  }

  @media ${device.laptop} {
    width: 60em;
    height: auto;
    box-shadow: 0 12px 30px 30px rgb(36 51 89 / 5%);
    border-radius: ${borderRadius};
    font-size: 1em;
  }
`;

namespace LoginNotification {
  const Wrapper = styled(Flex)<{
    hidden: boolean;
    variant?: "error" | "success";
  }>`
    position: absolute;
    top: 0;
    width: 100%;
    ${(props) =>
      props.variant === "error" ? `background: ${colours.red}` : ""};
    ${(props) =>
      props.variant === "success" ? `background: ${colours.green}` : ""};
    color: ${colours.white};
    display: flex;
    align-items: center;
    justify-content: flex-start;
    transition: max-height 0.3s ease-in-out;
    max-height: ${(props) => (props.hidden ? "0px" : "100px")};
    overflow: hidden;

    @media ${device.laptop} {
      border-top-right-radius: 10px;
    }
  `;

  const Title = styled.p`
    padding: 1em 3em;
    margin: 0;
  `;

  export const Notfication: FunctionComponent<{}> = () => {
    const { notification } = useGlobalNotifications();
    const [actualVariant, setActualVariant] = useState<"error" | "success">();
    useEffect(() => {
      if (notification) {
        setActualVariant(notification.variant);
      }
      if (!notification) {
        setTimeout(() => setActualVariant(undefined));
      }
    }, [notification]);
    return (
      <Wrapper hidden={!notification} variant={actualVariant}>
        <Title>{notification?.title || ""}</Title>
      </Wrapper>
    );
  };
}

export namespace Form {
  const fields = css`
    margin-top: 2em;
  `;

  export const Form = styled(Flex).attrs({ center: true, as: "form" })`
    flex-direction: column;
    width: 16em;
  `;
  export const Wrapper = styled(Flex).attrs({ center: true })`
    background: ${colours.darkWhite};
    flex-shrink: 1;
    padding: 2em;
    font-size: 1.2em;
    position: relative;
    flex-basis: 70%;

    @media ${device.tablet} {
      flex-basis: 50%;
      border-top-right-radius: ${borderRadius};
      border-bottom-right-radius: ${borderRadius};
    }

    @media ${device.laptop} {
      padding: 4.5em;
    }

    @media ${device.laptopL} {
      font-size: 0.9em;
    }
  `;
  export const Smaller = styled.p`
    font-size: 0.8em;
    color: ${colours.darkGrey};
  `;

  export const ForgotPassword = styled.a`
    margin: 1em 0;
    display: block;
  `;

  export const Login = styled(PrimaryButton).attrs({
    variant: "contained",
    fullWidth: true,
    type: "submit",
  })`
    margin: 1em 0;
  `;

  export const TextField = styled(UnstyledTextField)`
    ${fields}
  `;

  export const DateField = styled(UnstyledDateField)`
    ${fields}
  `;

  export const Country = styled(UnstyledCountry)`
    ${fields}
  `;
  export const SelectWrapper = styled.div`
    ${fields}
    width: 100%;
  `;

  export const PasswordField = styled(UnstyledPassword)`
    ${fields}
  `;

  export const CheckboxField = styled(UnstyledCheckbox)`
    ${fields}

    .MuiFormControlLabel-label {
      font-size: 0.9em;
    }
  `;
}

export const BaseLogin: React.FunctionComponent<{
  children: React.ReactNode;
}> = ({ children, ...props }) => {
  useDefaultToEnglish();
  return (
    <div style={{ height: "100%", overflow: "auto" }}>
      <Overlay {...props}>
        <Container>
          <Logo.Container>
            <Logo.LoginBackground />
            <Logo.Logo />
          </Logo.Container>
          <Form.Wrapper>
            <LoginNotification.Notfication />
            <UIView />
          </Form.Wrapper>
        </Container>
      </Overlay>
    </div>
  );
};
